import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { LaunchBulkUpdatePurchaseCurrencyCode } from 'src/components/Purchases/PurchaseActionDropdown/components/BulkUpdatePurchaseCurrencyCodeAction/LaunchBulkUpdatePurchaseCurrencyCode';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { usePurchaseDataContext } from 'src/contexts/PurchaseDataContext';
import { InfoMessage } from 'src/core/POS/MessageWithIcon/InfoMessage';
import { BulkUpdatePurchaseCurrencyCodeDialog } from 'src/dialogs/BulkEdits/BulkUpdatePurchaseCurrencyCodeDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditPurchaseClient,
  CommandOptionType,
  Feature,
  PurchaseOrderQuery,
} from 'src/WebApiController';

import { PURCHASE_BULK_UPDATE_PURCHASE_CURRENCY_CODE_KEY } from '../../PurchaseActionDropdown.constants';

export const useBulkUpdatePurchaseCurrencyCode = (
  filterQuery: PurchaseOrderQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  groupId?: string,
  shouldRegister?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const updateKey = `${PURCHASE_BULK_UPDATE_PURCHASE_CURRENCY_CODE_KEY}${
    groupId ? `-${groupId}` : ''
  }`;

  const { showErrorDialog } = useClientLogContext();
  const { refreshData } = usePurchaseDataContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const {
    openDialog: openBulkUpdatePurchaseCurrencyCode,
    closeDialog: closeBulkMarkPurchaseAsPaidDialog,
  } = useDialog(
    DialogId.BulkUpdatePurchaseCurrencyCode,
    BulkUpdatePurchaseCurrencyCodeDialog
  );

  const onSubmit = useCallback(
    async (
      supportBackgroundProcess: boolean,
      onPreviewReceived:
        | ((preview: BulkEditPreviewWithDetails) => void)
        | undefined,
      preview: BulkEditPreviewWithDetails | undefined,
      currencyCode: string
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const bulkEditClient = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const preview =
              await bulkEditClient.bulkUpdateCurrencyCodePreview(filterQuery);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateCurrencyCodePreview', error, {
              trackErrorData: { filterQuery },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const bulkEditClient = new BulkEditPurchaseClient(
              activeAccountWebClientConfig
            );

            const succeeded = await bulkEditClient.bulkUpdateCurrencyCode(
              {
                item1: preview!.preview,
                item2: currencyCode,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshData(true);
                await refreshExpandedListItems();
              }

              closeBulkMarkPurchaseAsPaidDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateCurrencyCode', error, {
              trackErrorData: {
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      filterQuery,
      refreshData,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
      closeBulkMarkPurchaseAsPaidDialog,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkUpdatePurchaseCurrencyCode({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      entityType: ActionOutboxEntityType.Purchase,
      infoMessage: (
        <InfoMessage
          message={<Content id={ContentId.UpdatePurchaseCurrency} />}
        />
      ),
      onCancel: closeBulkMarkPurchaseAsPaidDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.PurchasesBulkUpdatePurhcaseCurrencyCode,
        onSelect: openDialogStableRef,
      },
      disabled: !shouldRegister,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.PurchasesBulkUpdatePurhcaseCurrencyCode,
        disabled: !shouldRegister,
      });
    };
  }, [shouldRegister, openDialogStableRef, registerCommand, unregisterCommand]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      openBulkUpdatePurchaseCurrencyCode({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        entityType: ActionOutboxEntityType.Purchase,
        infoMessage: (
          <InfoMessage
            message={<Content id={ContentId.UpdatePurchaseCurrency} />}
          />
        ),
        onCancel: closeBulkMarkPurchaseAsPaidDialog,
      });
    },
    [
      closeBulkMarkPurchaseAsPaidDialog,
      onSubmit,
      openBulkUpdatePurchaseCurrencyCode,
      setIsLoading,
      updateKey,
    ]
  );

  const hasBulkUpdatePurchaseCurrencyFeature = useUserHasFeature(
    Feature.BulkUpdatePurchaseCurrency
  );
  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasBulkUpdatePurchaseCurrencyFeature) {
      return [];
    }

    return [
      {
        title: ContentId.CancelTicketGroupsParenth,
        onClick: onClick,
        disabled: isLoading,
      },
    ];
  }, [hasBulkUpdatePurchaseCurrencyFeature, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchBulkUpdatePurchaseCurrencyCode
        key="purchaseBulkUpdateCurrencyCode"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
